/**
 * Which decision stage a site is in, read from the persisted composite.
 *
 * `resolveDecisionStages` (compositeScorer) is computed for every site —
 * including one whose power strategy is undeclared, where the composite's
 * `decisionHold` is `power-strategy-undeclared` rather than a two-stage hold —
 * so the per-stage checklist is the reliable signal for "has early screening
 * finished?". Presence-based, exactly like the hold: currency stays the plan's
 * and the rollup's stricter job.
 */
import type { ScreeningOutcome, SiteComposite } from '../../types/portfolioComposite';

export interface SiteDecisionStageStatus {
  /** 1 while any required early-screening analysis is missing, else 2. */
  currentStage: 1 | 2;
  stage1Complete: boolean;
  /** Stage 1 result once complete; null before then or when not computed. */
  screeningOutcome: ScreeningOutcome | null;
  /** The accountable grid / hybrid / BTM-only strategy has been declared. */
  strategyDeclared: boolean;
  gridOnly: boolean;
  /** Stage 2 still needs a promoted generation scenario. */
  scenarioPending: boolean;
}

/** Older composites without per-stage data read as Stage 1 (today's behaviour). */
const STAGE_UNKNOWN: SiteDecisionStageStatus = {
  currentStage: 1,
  stage1Complete: false,
  screeningOutcome: null,
  strategyDeclared: false,
  gridOnly: false,
  scenarioPending: true,
};

export function resolveSiteDecisionStage(
  composite: Pick<SiteComposite, 'decisionStages'> | null | undefined,
): SiteDecisionStageStatus {
  const stages = composite?.decisionStages;
  if (!stages) return STAGE_UNKNOWN;
  const screening = stages.screening;
  const stage1Complete = screening.requiredTypes.length > 0 && screening.missingTypes.length === 0;
  return {
    currentStage: stage1Complete ? 2 : 1,
    stage1Complete,
    screeningOutcome: stage1Complete ? screening.outcome ?? null : null,
    strategyDeclared: stages.strategyDeclared,
    gridOnly: stages.gridOnly,
    scenarioPending: stages.validation.locked,
  };
}
